"use client";

import clsx from "clsx";
import { useState } from "react";
import { Modal } from "@/components/Dialog";
import { useI18n } from "@/i18n/I18nProvider";
import { Icon } from "../icons";
import { CloseButton } from "../PhonePopup";
import { usePopup } from "../usePopup";
import { SettingsPage } from "../settings/SettingsPage";
import { Lexicon } from "../lexicon/Lexicon";
import { ProfileCard } from "./ProfileCard";

/** What the account menu opened over the page. */
type Opened = "profile" | "settings" | "lexicon" | null;
const ITEM = "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] hover:bg-surface-2";

/** The account menu, always visible with the user's name: the profile, the settings and the reference (each a window), signing out. */
export function AccountMenu({ name, email, isAdmin, onSignOut, className }: { name: string; email: string; isAdmin: boolean; onSignOut: () => void; className?: string }) {
  const { t } = useI18n();
  const { open, setOpen, ref: root } = usePopup<HTMLDivElement>();
  const [shown, setShown] = useState<Opened>(null);
  const show = (w: Opened) => {
    setOpen(false);
    setShown(w);
  };
  return (
    <div ref={root} className={clsx("relative", className)}>
      <button onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={`${t("ws.acct.menu")}: ${name}`} className="flex h-7 cursor-pointer items-center gap-1.5 px-1 text-[12px] transition-opacity hover:opacity-80">
        <Icon name="user" className="h-3.5 w-3.5 text-muted" />
        <span className="max-w-[160px] truncate font-semibold text-ink">{name}</span>
        <Icon name="chevronDown" className="h-3.5 w-3.5 text-muted" />
      </button>
      {open && (
        <div role="menu" aria-label={t("ws.acct.menu")} className="absolute top-full right-0 z-40 mt-1 w-[260px] rounded-xl border border-line bg-surface p-1.5 shadow-lift">
          <button role="menuitem" onClick={() => show("profile")} className={ITEM}>
            <Icon name="user" className="h-4 w-4 text-muted" />
            {t("profile.title")}
          </button>
          <button role="menuitem" onClick={() => show("settings")} className={ITEM}>
            <Icon name="settings" className="h-4 w-4 text-muted" />
            {t("ws.acct.settings")}
          </button>
          <button role="menuitem" onClick={() => show("lexicon")} className={ITEM}>
            <Icon name="book" className="h-4 w-4 text-muted" />
            {t("ws.acct.lexicon")}
          </button>
          <div className="my-1 border-t border-line" />
          <button role="menuitem" onClick={onSignOut} className={ITEM} title={email}>
            <Icon name="logout" className="h-4 w-4 text-muted" />
            <span className="min-w-0">
              <span className="block">{t("nav.signOut")}</span>
              <span className="block truncate text-[11px] text-muted">{email}{isAdmin ? ` · ${t("nav.admin")}` : ""}</span>
            </span>
          </button>
        </div>
      )}
      {shown === "profile" && <ProfileDialog onClose={() => setShown(null)} />}
      {shown === "settings" && <SettingsDialog onClose={() => setShown(null)} />}
      {shown === "lexicon" && <LexiconDialog onClose={() => setShown(null)} />}
    </div>
  );
}

/** The profile over the page: a window in the middle. */
function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Modal label={t("profile.title")} onClose={onClose} size="wide">
      <ProfileCard onClose={onClose} />
    </Modal>
  );
}

/**
 * The settings over the page: a large window in the middle (up to 1100 px wide, 88 % of the window high; phones: 92 %)
 * — its title and close button stay, the settings scroll below them.
 */
function SettingsDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Modal label={t("ws.acct.settings")} onClose={onClose} size="large">
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-2.5">
        <Icon name="settings" className="h-4 w-4 text-muted" />
        <h2 className="text-[14px] font-semibold">{t("ws.acct.settings")}</h2>
        <CloseButton onClose={onClose} className="ml-auto" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 scrollbar-thin wide:p-5">
        <SettingsPage />
      </div>
    </Modal>
  );
}

/** The reference over the page: a large window like the settings — its title (with what it covers) and close button stay, the entries scroll below. */
function LexiconDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Modal label={t("lex.title")} onClose={onClose} size="large">
      <div className="flex shrink-0 items-start gap-2 border-b border-line px-4 py-2.5">
        <Icon name="book" className="mt-0.5 h-4 w-4 text-muted" />
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold">{t("lex.title")}</h2>
          <p className="text-[11px] text-muted">{t("lex.subtitle")}</p>
        </div>
        <CloseButton onClose={onClose} className="ml-auto" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 scrollbar-thin wide:p-5">
        <Lexicon />
      </div>
    </Modal>
  );
}
